import { create } from 'zustand';

interface LoadingState {
  auth: boolean;
  subscription: boolean;
  profile: boolean;
  betaStatus: boolean;
}

interface LoadingStore {
  loading: LoadingState;
  setLoading: (key: keyof LoadingState, value: boolean) => void;
  isAnyLoading: () => boolean;
  reset: () => void;
}

const initialState: LoadingState = {
  auth: true,
  // Avoid flashing dashboard skeletons on route remount; subscription state is cached separately.
  subscription: false,
  profile: false,
  betaStatus: false,
};

export const useLoadingState = create<LoadingStore>((set, get) => ({
  loading: { ...initialState },
  
  setLoading: (key, value) => {
    set((state) => ({
      loading: { ...state.loading, [key]: value }
    }));
  },
  
  isAnyLoading: () => {
    const { loading } = get();
    return Object.values(loading).some(v => v === true);
  },
  
  reset: () => set({ loading: { ...initialState } }),
}));

// Hooks for specific loading states
export const useAuthLoading = () => {
  const loading = useLoadingState(state => state.loading.auth);
  const setLoading = useLoadingState(state => state.setLoading);
  return [loading, (value: boolean) => setLoading('auth', value)] as const;
};

export const useSubscriptionLoading = () => {
  const loading = useLoadingState(state => state.loading.subscription);
  const setLoading = useLoadingState(state => state.setLoading);
  return [loading, (value: boolean) => setLoading('subscription', value)] as const;
};

export const useProfileLoading = () => {
  const loading = useLoadingState(state => state.loading.profile);
  const setLoading = useLoadingState(state => state.setLoading);
  return [loading, (value: boolean) => setLoading('profile', value)] as const;
};

export const useBetaStatusLoading = () => {
  const loading = useLoadingState(state => state.loading.betaStatus);
  const setLoading = useLoadingState(state => state.setLoading);
  return [loading, (value: boolean) => setLoading('betaStatus', value)] as const;
};
